import React, { useState, useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { fetchFarm, fetchObservations, fetchFrostHistory } from "@/lib/api";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend,
} from "recharts";
import { CalendarDays } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmt } from "@/lib/risk";
import { cn } from "@/lib/utils";

const PRESETS = [
    { key: "7d",  label: "7d",  days: 7 },
    { key: "30d", label: "30d", days: 30 },
    { key: "growing", label: "Season", days: 214 },
    { key: "12m", label: "12m", days: 365 },
];

const METRICS = [
    { key: "rain",            label: "Rainfall",         unit: "mm",   type: "bar",  daily: "rain", hourly: "rain" },
    { key: "rain_7d",         label: "7-day rainfall",   unit: "mm",   type: "line", derived: "rolling", window: 7, source: "rain" },
    { key: "rain_monthly",    label: "Monthly rainfall", unit: "mm",   type: "bar",  derived: "monthly", source: "rain" },
    { key: "rain_annual",     label: "Annual rainfall",  unit: "mm",   type: "kpi",  derived: "annual", source: "rain" },
    { key: "max_min_temp",    label: "Max / Min Temp",   unit: "°C",   type: "range", daily: "max_temp", daily2: "min_temp" },
    { key: "min_temp",        label: "Minimum temp",     unit: "°C",   type: "line", daily: "min_temp",  hourly: "temp" },
    { key: "avg_temp",        label: "Avg Temperature",  unit: "°C",   type: "line", daily: "avg_temp",  hourly: "temp" },
    { key: "wind",            label: "Wind",             unit: "km/h", type: "line", daily: "avg_wind",  hourly: "wind" },
    { key: "humidity",        label: "Humidity",         unit: "%",    type: "line", daily: "avg_humidity", hourly: "humidity" },
    { key: "frost_minutes",   label: "Frost minutes",    unit: "min",  type: "frost" },
];

const COLOURS = ["#2563EB", "#DC2626", "#16A34A", "#CA8A04", "#7C3AED", "#0F766E"];

const fmtX = (v, hourly) => {
    const d = new Date(v);
    return hourly
        ? d.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit" })
        : d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

function daysBetween(a, b) {
    return Math.round((new Date(b) - new Date(a)) / 86400000);
}

function todayStr() { return new Date().toISOString().slice(0, 10); }
function ago(days) {
    const d = new Date(); d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
}

export default function HistoryPage() {
    const [preset, setPreset] = useState("30d");
    const [dateFrom, setDateFrom] = useState(ago(30));
    const [dateTo, setDateTo] = useState(todayStr());
    const [metricKey, setMetricKey] = useState("rain");
    const [selectedStations, setSelectedStations] = useState(null); // null = all

    const { data: farm } = useQuery({ queryKey: ["farm"], queryFn: fetchFarm });
    const allStations = farm?.stations || [];
    const stations = useMemo(() => {
        if (!selectedStations) return allStations;
        return allStations.filter((s) => selectedStations.includes(s.id));
    }, [allStations, selectedStations]);

    // Fetch observations across the range for each selected station.
    const queries = useQueries({
        queries: stations.map((s) => ({
            queryKey: ["obs-range", s.id, dateFrom, dateTo],
            queryFn: () => fetchObservations(s.id, { date_from: dateFrom, date_to: dateTo }),
            enabled: !!farm,
        })),
    });
    // Frost history for the frost minutes chart.
    const { data: frost } = useQuery({
        queryKey: ["frost-history", daysBetween(dateFrom, dateTo)],
        queryFn: () => fetchFrostHistory(Math.max(7, daysBetween(dateFrom, dateTo) + 1)),
    });

    const applyPreset = (p) => {
        setPreset(p.key);
        setDateFrom(ago(p.days));
        setDateTo(todayStr());
    };

    if (!farm) return <div className="h-40 bg-white rounded-2xl border animate-pulse" />;

    const ready = queries.length === 0 || queries.every((q) => q.data);
    const activeMetric = METRICS.find((m) => m.key === metricKey);

    return (
        <div className="space-y-5" data-testid="history-page">
            <div>
                <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>History</h1>
                <p className="text-sm text-slate-500 mt-1">Compare historical data across stations and date ranges</p>
            </div>

            {/* Controls */}
            <div className="fw-card p-4 sm:p-5 space-y-4">
                <div className="flex flex-col md:flex-row gap-3 md:items-end">
                    <Tabs value={preset} onValueChange={(v) => applyPreset(PRESETS.find((p) => p.key === v))}>
                        <TabsList className="bg-slate-100 p-1">
                            {PRESETS.map((p) => (
                                <TabsTrigger key={p.key} value={p.key} data-testid={`history-preset-${p.key}`}>{p.label}</TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                    <div className="flex items-center gap-2 flex-wrap">
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><CalendarDays className="h-3 w-3" />From</label>
                        <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreset("custom"); }}
                            className="rounded-lg border border-slate-200 px-3 py-2 fw-metric text-sm" data-testid="history-from" />
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><CalendarDays className="h-3 w-3" />To</label>
                        <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPreset("custom"); }}
                            className="rounded-lg border border-slate-200 px-3 py-2 fw-metric text-sm" data-testid="history-to" />
                    </div>
                    <select value={metricKey} onChange={(e) => setMetricKey(e.target.value)}
                        data-testid="history-metric-select"
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium ml-auto">
                        {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                    </select>
                </div>

                {/* Station filter */}
                <div className="flex flex-wrap gap-2 pt-1">
                    {allStations.map((s, i) => {
                        const active = !selectedStations || selectedStations.includes(s.id);
                        return (
                            <button key={s.id}
                                onClick={() => setSelectedStations((cur) => {
                                    const base = cur || allStations.map((x) => x.id);
                                    const next = base.includes(s.id) ? base.filter((x) => x !== s.id) : [...base, s.id];
                                    return next.length === allStations.length ? null : next;
                                })}
                                data-testid={`history-station-${s.id}`}
                                className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition",
                                    active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
                                <span className="h-2 w-2 rounded-full" style={{ background: COLOURS[i % COLOURS.length] }} />
                                {s.name}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Chart */}
            <div className="fw-card p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{activeMetric.label}</h2>
                        <p className="text-xs text-slate-500 fw-metric">{dateFrom} → {dateTo}</p>
                    </div>
                    <span className="text-xs text-slate-500 fw-metric">{activeMetric.unit}</span>
                </div>
                {!ready ? (
                    <div className="h-96 animate-pulse bg-slate-50 rounded-xl" />
                ) : (
                    <ChartFor metric={activeMetric} queries={queries} stations={stations} frost={frost} />
                )}
            </div>
        </div>
    );
}

function ChartFor({ metric, queries, stations, frost }) {
    if (metric.type === "frost") {
        return <FrostMinutesChart frost={frost} stations={stations} />;
    }
    if (metric.key === "rain_annual") {
        return <AnnualRainKPIs queries={queries} stations={stations} />;
    }

    // Assemble rows for daily-based charts.
    const isHourly = queries[0]?.data?.granularity === "hourly";
    const xKey = isHourly ? "ts" : "date";

    const map = new Map();
    queries.forEach((q, i) => {
        const name = stations[i].name;
        (q.data?.data || []).forEach((row) => {
            const t = row[xKey];
            if (!map.has(t)) map.set(t, { [xKey]: t });
            const entry = map.get(t);
            if (metric.key === "max_min_temp") {
                entry[`${name}_max`] = row.max_temp;
                entry[`${name}_min`] = row.min_temp;
            } else if (metric.derived === "rolling") {
                entry[name] = row[metric.source];
            } else if (metric.derived === "monthly") {
                entry[name] = row[metric.source];
            } else {
                const k = isHourly ? metric.hourly : metric.daily;
                entry[name] = row[k];
            }
        });
    });
    let rows = Array.from(map.values()).sort((a, b) => a[xKey].localeCompare(b[xKey]));

    // Rolling 7d totals
    if (metric.derived === "rolling") {
        const window = metric.window;
        rows = rows.map((_, i, arr) => {
            const slice = arr.slice(Math.max(0, i - window + 1), i + 1);
            const out = { [xKey]: arr[i][xKey] };
            stations.forEach((s) => {
                out[s.name] = round1(slice.reduce((a, r) => a + (r[s.name] || 0), 0));
            });
            return out;
        });
    }

    // Monthly aggregation
    if (metric.derived === "monthly") {
        const monthly = new Map();
        rows.forEach((r) => {
            const m = r[xKey].slice(0, 7);
            if (!monthly.has(m)) monthly.set(m, { date: m });
            const entry = monthly.get(m);
            stations.forEach((s) => {
                entry[s.name] = round1((entry[s.name] || 0) + (r[s.name] || 0));
            });
        });
        rows = Array.from(monthly.values()).sort((a, b) => a.date.localeCompare(b.date));
    }

    const xFmt = metric.derived === "monthly"
        ? (v) => new Date(v + "-01").toLocaleDateString("en-AU", { month: "short", year: "2-digit" })
        : (v) => fmtX(v, isHourly);

    return (
        <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
                {metric.type === "bar" ? (
                    <BarChart data={rows} margin={{ top: 10, right: 20, left: -5, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={metric.derived === "monthly" ? "date" : xKey} tickFormatter={xFmt} tick={{ fontSize: 11 }} minTickGap={40} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={xFmt} contentStyle={tipStyle} />
                        <Legend />
                        {stations.map((s, i) => (
                            <Bar key={s.id} dataKey={s.name} fill={COLOURS[i % COLOURS.length]} radius={[4, 4, 0, 0]} />
                        ))}
                    </BarChart>
                ) : metric.type === "range" ? (
                    <BarChart data={rows} margin={{ top: 10, right: 20, left: -5, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={xKey} tickFormatter={xFmt} tick={{ fontSize: 11 }} minTickGap={40} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={xFmt} contentStyle={tipStyle} />
                        <Legend />
                        {stations.map((s, i) => (
                            <React.Fragment key={s.id}>
                                <Bar dataKey={`${s.name}_max`} name={`${s.name} max`} fill={COLOURS[i % COLOURS.length]} radius={[4, 4, 0, 0]} />
                                <Bar dataKey={`${s.name}_min`} name={`${s.name} min`} fill={COLOURS[i % COLOURS.length]} fillOpacity={0.4} radius={[4, 4, 0, 0]} />
                            </React.Fragment>
                        ))}
                    </BarChart>
                ) : (
                    <LineChart data={rows} margin={{ top: 10, right: 20, left: -5, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={xKey} tickFormatter={xFmt} tick={{ fontSize: 11 }} minTickGap={40} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={xFmt} contentStyle={tipStyle} />
                        <Legend />
                        {stations.map((s, i) => (
                            <Line key={s.id} type="monotone" dataKey={s.name}
                                stroke={COLOURS[i % COLOURS.length]} strokeWidth={2} dot={false} />
                        ))}
                    </LineChart>
                )}
            </ResponsiveContainer>
        </div>
    );
}

function AnnualRainKPIs({ queries, stations }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 py-2">
            {stations.map((s, i) => {
                const total = (queries[i]?.data?.data || []).reduce((a, r) => a + (r.rain || 0), 0);
                return (
                    <div key={s.id} className="p-4 rounded-xl border border-slate-200">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{s.name}</div>
                        <div className="fw-metric text-3xl font-extrabold text-slate-900 mt-1">
                            {fmt(total, 1)}<span className="text-base text-slate-400 font-semibold"> mm</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">Rainfall in selected range</div>
                    </div>
                );
            })}
        </div>
    );
}

function FrostMinutesChart({ frost, stations }) {
    if (!frost) return <div className="h-96 animate-pulse bg-slate-50 rounded-xl" />;
    const dates = [...frost.dates].reverse();
    const rows = dates.map((d) => {
        const row = { date: d };
        stations.forEach((s) => {
            const ev = frost.nights[s.id]?.by_date?.[d];
            row[s.name] = ev ? ev.minutes_below : 0;
        });
        return row;
    });
    return (
        <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ top: 10, right: 20, left: -5, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tickFormatter={(v) => new Date(v).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                        tick={{ fontSize: 11 }} minTickGap={40} />
                    <YAxis tick={{ fontSize: 11 }} unit="m" />
                    <Tooltip contentStyle={tipStyle} formatter={(v) => [`${v} min`, ""]} />
                    <Legend />
                    {stations.map((s, i) => (
                        <Bar key={s.id} dataKey={s.name} fill={COLOURS[i % COLOURS.length]} radius={[4, 4, 0, 0]} />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

const tipStyle = { background: "white", border: "1px solid #E2E8F0", borderRadius: 12, fontSize: 12 };
function round1(n) { return Math.round(n * 10) / 10; }
